import { Issue } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, User, AlertTriangle } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

interface IssueCardProps {
  issue: Issue;
  onStatusChange?: (id: string, status: Issue['status']) => void;
  onViewDetails?: (issue: Issue) => void;
  showActions?: boolean;
}

/* =========================
   RISK META
   ========================= */
const getRiskMeta = (score?: number) => {
  if (score === undefined) return null;
  if (score >= 70)
    return { label: 'High Risk', bar: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-300' };
  if (score >= 40)
    return { label: 'Medium Risk', bar: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
  return { label: 'Low Risk', bar: 'bg-green-500', badge: 'bg-green-100 text-green-700 border-green-300' };
};

export default function IssueCard({
  issue,
  onStatusChange,
  onViewDetails,
  showActions = false,
}: IssueCardProps) {
  const riskMeta = getRiskMeta(issue.riskScore);

  const daysOpen = differenceInDays(
    new Date(),
    new Date(issue.reportedAt)
  );

  const getStatusColor = (status: Issue['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: Issue['category']) => {
    switch (category) {
      case 'pothole':
        return 'bg-red-100 text-red-800';
      case 'street_light':
        return 'bg-yellow-100 text-yellow-800';
      case 'garbage':
        return 'bg-green-100 text-green-800';
      case 'traffic_sign':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: Issue['priority']) => {
    switch (priority) {
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <AlertTriangle className="h-4 w-4 text-green-500" />;
    }
  };

  return (
    <Card
      className={`transition-all hover:shadow-lg ${
        issue.riskScore && issue.riskScore >= 70 ? 'ring-2 ring-red-200 animate-pulse-slow' : ''
      }`}
    >
      {/* ================= HEADER ================= */}
      <CardHeader className="pb-3 space-y-2">
        <div className="flex justify-between items-start gap-2">
          <CardTitle className="text-lg leading-tight">
            {issue.title}
          </CardTitle>

          <div className="flex flex-col items-end gap-1">
            {riskMeta && (
              <Badge variant="outline" className={riskMeta.badge}>
                {riskMeta.label}
              </Badge>
            )}
            <Badge className={getStatusColor(issue.status)}>
              {issue.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className={getCategoryColor(issue.category)}>
            {issue.category.replace('_', ' ')}
          </Badge>

          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {getPriorityIcon(issue.priority)}
            <span className="capitalize">{issue.priority} priority</span>
          </div>
        </div>

        {/* ================= RISK BAR ================= */}
        {issue.riskScore !== undefined && riskMeta && (
          <div>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Risk Score</span>
              <span>{issue.riskScore}/100</span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full ${riskMeta.bar}`}
                style={{ width: `${issue.riskScore}%` }}
              />
            </div>
          </div>
        )}
      </CardHeader>

      {/* ================= CONTENT ================= */}
      <CardContent className="space-y-3">
        {issue.image && (
          <div className="relative overflow-hidden rounded-lg border">
            <img
              src={issue.image}
              alt={issue.title}
              className="w-full h-48 object-cover"
            />
            <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              Evidence
            </span>
          </div>
        )}

        <p className="text-sm text-gray-600 line-clamp-2">
          {issue.description}
        </p>

        {/* ================= META ================= */}
        <div className="grid grid-cols-2 gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            <span className="truncate">
              {issue.location.address || 'Location available'}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>{format(issue.reportedAt, 'MMM dd, yyyy')}</span>
          </div>

          <div className="flex items-center gap-1 col-span-2">
            <User className="h-4 w-4" />
            <span>Reported by {issue.reportedBy}</span>
          </div>
        </div>

        {/* ================= SLA ================= */}
        {issue.status !== 'resolved' && (
          <div className="text-xs text-muted-foreground">
            ⏱ Open for <span className="font-medium">{daysOpen}</span> day(s)
          </div>
        )}

        {/* ================= ACTIONS ================= */}
        <div className="flex justify-between items-center pt-3">
          <Button variant="outline" size="sm" onClick={() => onViewDetails?.(issue)}>
            View Full Report
          </Button>

          {showActions && onStatusChange && (
            <div className="flex gap-2">
              {issue.status === 'pending' && (
                <Button size="sm" onClick={() => onStatusChange(issue.id, 'in_progress')}>
                  Start Work
                </Button>
              )}
              {issue.status === 'in_progress' && (
                <Button size="sm" onClick={() => onStatusChange(issue.id, 'resolved')}>
                  Mark Resolved
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* =========================
   ANIMATION
   ========================= */
const styles = `
@keyframes pulseSlow {
  0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
  70% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
  100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
}
.animate-pulse-slow {
  animation: pulseSlow 2.5s infinite;
}
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}
